/*
 * Plain-language labels of a build (pure, no React): statuses, how it is built and the phases of its timeline. The
 * words are for client admins and developers, not for Hangar's engineers: no internal mode or phase names, and no
 * provider names (SPEC decision 23): clients read "the build service" and "Built by Hangar". `serviceName` names the
 * provider for operator-only surfaces (the provider link of a build).
 */
import type { BuildStatus, ExecutionMode } from '@hangar/shared';
import { EXECUTION_MODE_LABEL } from '@/components/badges';
import { humanize } from '@/lib/format';

const SERVICE: Record<string, string> = { eas: 'Expo', bitrise: 'Bitrise' };

/** "Expo" / "Bitrise", or "the build service" when the provider is unknown: for operator-only surfaces. */
export const serviceName = (kind?: string | null): string => (kind ? SERVICE[kind] : undefined) ?? 'the build service';

/** Status labels when the provider is not known (status filter, lists of statuses). */
export const STATUS_LABEL: Record<BuildStatus, string> = {
  pending_approval: 'Waiting for approval',
  awaiting_client: 'Waiting for your CI',
  queued: 'Queued',
  dispatching: 'Sending to the build service',
  provider_queued: 'Waiting in the build queue',
  running: 'Building',
  verifying: 'Running checks',
  succeeded: 'Succeeded',
  failed: 'Failed',
  errored: 'Error',
  canceled: 'Canceled',
  timed_out: 'Timed out',
  expired: 'Expired',
};

/** Shorter labels for narrow columns (tablet table). */
export const STATUS_LABEL_SHORT: Record<BuildStatus, string> = {
  ...STATUS_LABEL,
  pending_approval: 'Approval',
  awaiting_client: 'Waiting for CI',
  dispatching: 'Sending',
  provider_queued: 'Waiting',
  verifying: 'Checking',
};

/** "Waiting in the build queue", "Sending to the build service", "Building"…: the status in plain words. */
export const statusLabel = (status: BuildStatus): string => STATUS_LABEL[status];

/** A status label that also accepts statuses this console does not know (timeline events). */
export const anyStatusLabel = (status: string): string => (status in STATUS_LABEL ? statusLabel(status as BuildStatus) : humanize(status));

/** How the build is made: Hangar builds it, Hangar's build service builds it on request, or the client's CI starts it. */
export const executionModeLabel = (mode: ExecutionMode): string => EXECUTION_MODE_LABEL[mode] ?? humanize(mode);

/** Phase names reported by Hangar's runner, the provider and the CI client, in plain words. */
const PHASE_LABEL: Record<string, string> = {
  runner_launch: 'Start build machine',
  runner_checkout: 'Fetch code',
  runner_install: 'Install dependencies',
  eas_submit: 'Send to the build service',
  bitrise_submit: 'Send to the build service',
  client_start: 'Start in your CI',
  attach: 'Link the build',
};

/** "Fetch code" for `runner_checkout`; an unknown phase is humanized ("upload_symbols" → "Upload symbols"). */
export const phaseLabel = (name: string): string => PHASE_LABEL[name] ?? humanize(name);

const PHASE_RESULT: Record<string, string> = { passed: 'done', failed: 'failed', error: 'failed', skipped: 'skipped', started: 'started' };

/** "Fetch code · done": a phase event's title. */
export const phaseEventTitle = (name: string | undefined, result: string): string => `${name ? phaseLabel(name) : 'Step'} · ${PHASE_RESULT[result] ?? humanize(result).toLowerCase()}`;

const SOURCE_LABEL: Record<string, string> = { ui: 'Console', sdk: 'SDK', api: 'API', scm_webhook: 'Git push', schedule: 'Schedule', redeploy: 'Redeploy', promotion: 'Promotion' };

/** Where the build request came from ("Console", "Git push"…). */
export const sourceLabel = (source: string): string => SOURCE_LABEL[source] ?? humanize(source);
